import { BudgetReferenceValueResponse } from '../models/BudgetReferenceValueResponse';
import { BudgetResponse } from '../models/BudgetResponse';
import { BudgetSlpResponse } from '../models/BudgetSlpResponse';
import { ConsumptionUnitEnum } from '../enum/ConsumptionUnitEnum';
import { PeriodEnum } from '../enum/PeriodEnum';
import { SDPTariffChoiceEnum } from '../enum/SDPTariffChoiceEnum';
import axiosBearerClient from '../axiosBearerClient';
import { useQuery } from '@tanstack/react-query';

export const useGetConsumptionHistory = (
  sdpId?: string,
  year?: number,
  unit?: ConsumptionUnitEnum
) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['budgets-history', sdpId, year],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId && !!year && unit === ConsumptionUnitEnum.KWH,
    queryFn: async () => {
      return await axiosBearerClient.get<BudgetResponse[]>(
        `/budgets/histories/consumptions/${sdpId}?year=${year}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetBudgetHistory = (sdpId?: string, year?: number, unit?: ConsumptionUnitEnum) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['budgets-history', sdpId, year],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId && !!year && unit === ConsumptionUnitEnum.EURO,
    queryFn: async () => {
      return await axiosBearerClient.get<BudgetResponse[]>(
        `/budgets/histories/currency/${sdpId}?year=${year}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetBudgetByPeriod = (
  sdpId?: string,
  unit?: ConsumptionUnitEnum,
  period?: PeriodEnum
) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['budgets-period', sdpId, period, unit],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId && !!period && !!unit,
    queryFn: async () => {
      return await axiosBearerClient.get<BudgetResponse>(
        `/budgets/${sdpId}?period=${period}&type=${unit}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetBudgetReference = (sdpId?: string) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['budgets-reference-values', sdpId],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId,
    queryFn: async () => {
      return await axiosBearerClient.get<BudgetReferenceValueResponse>(
        `/budgets/reference-values/${sdpId}`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetBudgetSlp = (sdpId?: string, tariff?: SDPTariffChoiceEnum) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['budgets-slp', sdpId, tariff],
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: false,
    enabled: !!sdpId && !!tariff,
    queryFn: async () => {
      return await axiosBearerClient.get<BudgetSlpResponse[]>(
        `/budgets/slps/${sdpId}?type=${
          tariff === SDPTariffChoiceEnum.SOCIAL ? ConsumptionUnitEnum.EURO : ConsumptionUnitEnum.KWH
        }`
      );
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};
